// Libs
import { FieldType, ViewModelBase } from "@shoothill/core";
import { action, computed, runInAction } from "mobx";
import type { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";
import { Stores } from "Globals/Stores";

// Models
import { PersonModel } from "Globals/Models/Domain/FormSections/PropertyDetails/PersonModel";
import { PropertyDetailPersonDTO } from "Globals/Models/DTO/PropertyDetailPersonDTO";

// ViewModels
import PropertyDetailsViewModel from "Globals/ViewModels/FormSections/PropertyDetails/PropertyDetailsViewModel";
import ApplicationFormViewModel from "Globals/ViewModels/ApplicationFormViewModel";

export default class PersonViewModel extends ViewModelBase<PersonModel> {
    // region Fields
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;
    // endregion

    constructor(personModel?: PersonModel) {
        super(personModel ? personModel : new PersonModel());
    }

    // region Display Logic
    @computed
    public get genderDisplayName(): string {
        if (!this.model.gender) return "Unknown";
        const genderOptions = StoresInstance.Domain.LookupStore.getGenderOptions;
        return genderOptions.find((g) => g.value === this.model.gender)?.key ?? "Unknown";
    }

    @computed
    public get relationshipDisplayName(): string {
        if (!this.model.relationshipTypeId) return "Unknown";
        return StoresInstance.Domain.RelationshipTypeStore.getRelationshipTypeName(this.model.relationshipTypeId);
    }

    @computed
    public get getFormattedDateOfBirth(): string {
        return moment.utc(this.model.dateOfBirth).local().format("DD/MM/YYYY");
    }

    @computed
    public get isModelDirty(): boolean {
        return Object.keys(this.model).some((field) => this.model.getDirty(field as keyof FieldType<PersonModel>));
    }

    public IsDisabled(applicationFormViewModel: ApplicationFormViewModel, storesContext: Stores): boolean {
        return this.IsLoading || applicationFormViewModel.model.isSubmitted || storesContext.Domain.SaveStore.isSaving;
    }
    // endregion

    // region Field Handling
    @action
    public handleFieldChange = (fieldName: keyof FieldType<PersonModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public setDate(fieldName: keyof FieldType<PersonModel>, val: MaterialUiPickersDate): void {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
    }
    // endregion

    // region Dirty State Management
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<PersonModel>, value);
        });
    }
    // endregion

    // region Validation
    public async isFieldValid(fieldName: keyof FieldType<PersonModel>, value: any): Promise<boolean> {
        const { isValid, errorMessage } = await this.validateDecorators(fieldName);
        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }
    // endregion

    // region Persistence
    @action
    public stash = async (propertyDetails: PropertyDetailsViewModel): Promise<void> => {
        const isValid = await this.isModelValid();

        if (isValid) {
            const model = new PersonModel();
            model.fromDto(this.model);
            const viewModel = new PersonViewModel(model);

            runInAction(() => {
                propertyDetails.personViewModels.push(viewModel);
                propertyDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (propertyDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.propertyDetailId = propertyDetailId;

            const dto: PropertyDetailPersonDTO = this.model.toDto();
            const apiResult = await this.Post<PropertyDetailPersonDTO>(AppUrls.Server.ApplicationForm.Upsert.Person, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("Person:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("Person:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            console.log("Could not save from Stash.", exception);
            return Promise.reject(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (propertyDetails: PropertyDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("propertyDetailId")) {
                this.deleteFromStash(propertyDetails);
            } else {
                await this.deleteFromDatabase(propertyDetails);
            }
        } catch (exception) {
            console.error("Could not delete person.", exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    private deleteFromStash(propertyDetails: PropertyDetailsViewModel): void {
        runInAction(() => {
            const index = propertyDetails.personViewModels.findIndex((vm) => vm.getValue("id") === this.model.id);
            if (index > -1) {
                propertyDetails.personViewModels.splice(index, 1);
            }
        });
    }

    @action
    private async deleteFromDatabase(propertyDetails: PropertyDetailsViewModel): Promise<void> {
        this.setIsLoading(true);
        this.saveStore.setIsSaving(true);

        this.model.propertyDetailId = propertyDetails.model.id ?? "";
        this.model.isDeleted = true;

        const upsertPerson: PropertyDetailPersonDTO = this.model.toDto();
        const apiResult = await this.Post<PropertyDetailPersonDTO>(AppUrls.Server.ApplicationForm.Upsert.Person, upsertPerson);

        if (apiResult.wasSuccessful) {
            this.model.fromDto(apiResult.payload);
            console.info("Person:Upsert-Successful");

            runInAction(() => {
                propertyDetails.personViewModels = propertyDetails.personViewModels.filter((a) => !a.model.isDeleted);
            });

            this.model.resetModel();
            this.setModelDirty(false);
            return Promise.resolve();
        } else {
            console.error("Person:Upsert-Error", apiResult.errors);
            return Promise.reject(apiResult.errors);
        }
    }
    // endregion

    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
